import { useCallback, useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SearchInput } from '@/components/ui/input'
import { AffiliateApiClient } from '@/utils/api/affiliateApi'
import { Page } from '@progress/kendo-react-dropdowns'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import {
  AuditLog,
  AuditLogData,
  isAuditLogNameDto,
  isAuditLogNameDtoId,
  isAuditLogNameDtoName,
} from 'api/Clients/Types/AuditLog'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { DocumentApiClient } from 'utils/api/documentApi'
import { ExchangeRatesApiClient } from 'utils/api/exchangeRatesApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { LeadsApiClient } from 'utils/api/leadsApi'
import { MetaTraderApiClient, PlatformType } from 'utils/api/metaTraderApi'
import { NotificationsApiClient } from 'utils/api/notificationsApi'
import { NPaymentsApi } from 'utils/api/nPaymentsApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TicketsApiClient } from 'utils/api/ticketsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { dateComparator, formatLocalDateUTC } from 'utils/date'
import { isEmpty, isNotEmpty } from 'utils/is-empty'
import { isNotObject } from 'utils/is-object'
import { isString } from 'utils/is-string'
import { getPaginationInitialState } from 'utils/paginationInitialState'

import { Label } from '../ui/label'

import styles from './ClientAreaAuditLog.module.scss'

export interface ClientAreaAuditLogProps {
  clientId?: string
  isCurrencyMarkup?: boolean
  tradingAccountId?: string
  tradingAccountLeverageId?: string
  transactionId?: string
  documentId?: string
  bankAccountId?: string
  cardDetailsId?: string
  introducingBrokerId?: string
  ticketId?: string
  countryRiskId?: string
  leadId?: string
  roleId?: string
  teamId?: string
  demoAccountId?: string
  welcomeAccountId?: string
  marketingCampaignId?: string
  administrationUserId?: string
  paymentAgentId?: string
  ibProfileId?: string
  lossPercentageId?: string
  mt4Configuration?: { serverId: string; symbolId: string }
  mt5Configuration?: { serverId: string; symbolId: string }
  introducingBrokerRebatesId?: string
  walletId?: string
  countryId?: string
  subscriptionId?: string
  pspMethodId?: string
  pspGroupMethodId?: string
  paymentProviderThresholdId?: string
  ibLandingId?: string
  referralMaterialId?: string
  referralMaterialsCategoryId?: string
  notificationId?: string
  promoMaterialId?: string
  paymentProviderId?: string
  platformDownloadLinkId?: string
  notificationContactDetailsId?: number
  bonusDepositId?: string
  affiliateId?: string
  testbuilderId?: string
  rebatePolicyId?: string
  rebateSettingsId?: string
  rewardId?: boolean
  rewardProgramId?: string
}

const useClientAreaAuditLogFetch = (props: ClientAreaAuditLogProps) => {
  const {
    clientId,
    isCurrencyMarkup,
    tradingAccountId,
    tradingAccountLeverageId,
    transactionId,
    documentId,
    bankAccountId,
    cardDetailsId,
    introducingBrokerId,
    ticketId,
    countryRiskId,
    leadId,
    roleId,
    teamId,
    demoAccountId,
    welcomeAccountId,
    marketingCampaignId,
    administrationUserId,
    paymentAgentId,
    ibProfileId,
    lossPercentageId,
    mt4Configuration,
    mt5Configuration,
    introducingBrokerRebatesId,
    walletId,
    countryId,
    subscriptionId,
    pspMethodId,
    pspGroupMethodId,
    paymentProviderThresholdId,
    ibLandingId,
    referralMaterialId,
    referralMaterialsCategoryId,
    notificationId,
    promoMaterialId,
    platformDownloadLinkId,
    paymentProviderId,
    notificationContactDetailsId,
    bonusDepositId,
    affiliateId,
    testbuilderId,
    rebatePolicyId,
    rebateSettingsId,
    rewardId,
    rewardProgramId,
  } = props

  const leadsApiClient = useApiClient(LeadsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const ticketsApiClient = useApiClient(TicketsApiClient)
  const documentApiClient = useApiClient(DocumentApiClient)
  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  const metaTraderApiClient = useApiClient(MetaTraderApiClient)
  const paymentsApi = useApiClient(NPaymentsApi)
  const notificationsApiClient = useApiClient(NotificationsApiClient)
  const affiliateApiClient = useApiClient(AffiliateApiClient)
  const exchangeRatesApiClient = useApiClient(ExchangeRatesApiClient)

  const fetchData = useCallback(async (): Promise<AuditLog[]> => {
    if (testbuilderId) {
      return await clientsApiClient.getTestbuilderEvents(testbuilderId)
    }
    if (clientId && !subscriptionId) {
      return await clientsApiClient.getClientLogs(clientId)
    }
    if (isCurrencyMarkup) {
      return await exchangeRatesApiClient.getExchangeRateData()
    }
    if (ibLandingId) {
      return await clientAreaApiClient.getLandingPagesEvents(ibLandingId)
    }
    if (referralMaterialId && promoMaterialId) {
      return await clientAreaApiClient.getPromoMaterialEvents(referralMaterialId, promoMaterialId)
    }
    if (promoMaterialId) {
      return await clientAreaApiClient.getPromoMaterialEventsForLandingPage(promoMaterialId)
    }
    if (referralMaterialId) {
      return await clientAreaApiClient.getReferralMaterialEvents(referralMaterialId)
    }
    if (referralMaterialsCategoryId) {
      return await clientAreaApiClient.getReferralMaterialsCategoryEvents(
        referralMaterialsCategoryId
      )
    }
    if (notificationId) {
      return await clientAreaApiClient.getNotificationsEvents(notificationId)
    }
    if (tradingAccountId) {
      return await tradingAccountsApiClient.getTradingAccountLogs(tradingAccountId)
    }
    if (tradingAccountLeverageId) {
      return await tradingAccountsApiClient.getInstrumentLeverageAdjustmentEvents(
        tradingAccountLeverageId
      )
    }
    if (transactionId) {
      return await walletsApiClient.getTransactionAuditLogs(transactionId)
    }
    if (documentId) {
      return await documentApiClient.getDocumentsAuditLogs(documentId)
    }
    if (bankAccountId) {
      return await paymentsApi.getBankAccountAuditLogs(bankAccountId)
    }
    if (cardDetailsId) {
      return await paymentsApi.getCardDetailsAuditLogs(cardDetailsId)
    }
    if (introducingBrokerId) {
      return await partnershipsApiClient.getIntroducingBrokerAuditLogs(introducingBrokerId)
    }
    if (ticketId) {
      return await ticketsApiClient.getTicketAuditLogs(ticketId)
    }
    if (countryRiskId) {
      return await clientsApiClient.getCountryComplianceEvents(countryRiskId)
    }
    if (leadId) {
      return await leadsApiClient.getLeadEvents(leadId)
    }
    if (roleId) {
      return await identityApiClient.getRoleEvents(roleId)
    }
    if (teamId) {
      return await identityApiClient.getTeamEvents(teamId)
    }
    if (welcomeAccountId) {
      return await tradingAccountsApiClient.getWelcomeAccountEvents(welcomeAccountId)
    }
    if (demoAccountId) {
      return await tradingAccountsApiClient.getDemoAccountEvents(demoAccountId)
    }
    if (marketingCampaignId) {
      return await tradingAccountsApiClient.getMarketingCampaignEvents(marketingCampaignId)
    }
    if (administrationUserId) {
      return await identityApiClient.getUserEvents(administrationUserId)
    }
    if (paymentAgentId) {
      return walletsApiClient.getPaymentAgentEvents(paymentAgentId)
    }
    if (ibProfileId) {
      return await partnershipsApiClient.getIntroducingBrokerProfileEvents(ibProfileId)
    }
    if (rebatePolicyId) {
      return await partnershipsApiClient.getIntroducingBrokerRebatePoliciesEvents(rebatePolicyId)
    }
    if (rebateSettingsId) {
      return await partnershipsApiClient.getIntroducingBrokerIBRebateSettingsEvents(
        rebateSettingsId
      )
    }
    if (lossPercentageId) {
      return clientsApiClient.getComplianceLossPercentageEvents(lossPercentageId)
    }
    if (mt4Configuration?.serverId && mt4Configuration?.symbolId) {
      return metaTraderApiClient.getPlatformSwaps(
        PlatformType.metatrader4,
        mt4Configuration.serverId,
        mt4Configuration.symbolId
      )
    }
    if (mt5Configuration?.serverId && mt5Configuration?.symbolId) {
      return metaTraderApiClient.getPlatformSwaps(
        PlatformType.metatrader5,
        mt5Configuration.serverId,
        mt5Configuration.symbolId
      )
    }
    if (introducingBrokerRebatesId) {
      return partnershipsApiClient.getIntroducingBrokerRebatesEvents(introducingBrokerRebatesId)
    }
    if (walletId) {
      return walletsApiClient.getWalletEvents(walletId)
    }
    if (countryId) {
      return clientsApiClient.getCountryEvents(countryId)
    }
    if (pspGroupMethodId) {
      return walletsApiClient.getPSPGroupEvents(pspGroupMethodId)
    }
    if (pspMethodId) {
      return walletsApiClient.getPSPEvents(pspMethodId)
    }
    if (paymentProviderThresholdId) {
      return walletsApiClient.getPaymentProviderThresholdEvents(paymentProviderThresholdId)
    }
    if (clientId && subscriptionId) {
      return tradingAccountsApiClient.getSubscriptionEvents(clientId, subscriptionId)
    }
    if (platformDownloadLinkId) {
      return tradingAccountsApiClient.getPlatformDownloadLinkEvents()
    }
    if (paymentProviderId) {
      return await paymentsApi.getPaymentProviderEvents(paymentProviderId)
    }
    if (notificationContactDetailsId) {
      return await notificationsApiClient.getNotificationsContactDetailsEvents(
        notificationContactDetailsId
      )
    }
    if (rewardId) {
      return await tradingAccountsApiClient.getRewardsEvent()
    }
    if (rewardProgramId) {
      return await tradingAccountsApiClient.getRewardProgramEvent(rewardProgramId)
    }
    if (bonusDepositId) {
      return await walletsApiClient.getBonusCampaignEvents(bonusDepositId)
    }
    if (affiliateId) {
      return await affiliateApiClient.getAffiliateEvents(affiliateId)
    }
    return []
  }, [
    clientId,
    tradingAccountId,
    transactionId,
    documentId,
    bankAccountId,
    cardDetailsId,
    introducingBrokerId,
    ticketId,
    countryRiskId,
    leadId,
    roleId,
    teamId,
    demoAccountId,
    welcomeAccountId,
    marketingCampaignId,
    administrationUserId,
    paymentAgentId,
    ibProfileId,
    lossPercentageId,
    mt4Configuration,
    mt5Configuration,
    introducingBrokerRebatesId,
    walletId,
    countryId,
    subscriptionId,
    pspMethodId,
    pspGroupMethodId,
    paymentProviderThresholdId,
    ibLandingId,
    referralMaterialId,
    referralMaterialsCategoryId,
    notificationId,
    promoMaterialId,
    paymentProviderId,
    notificationContactDetailsId,
    bonusDepositId,
    affiliateId,
    testbuilderId,
    rebatePolicyId,
    rebateSettingsId,
    rewardId,
  ])

  const { data, isLoading } = useFetchOne<AuditLog[]>(fetchData)

  return { data, isLoading }
}

const useAuditLogData = () => {
  const [auditLogData, setAuditLogData] = useState<AuditLogData | null>(null)

  const handleAuditLogModalOpen = (auditLog: AuditLogData) => {
    setAuditLogData(auditLog)
  }

  const handleAuditLogModalClose = () => {
    setAuditLogData(null)
  }

  return {
    handleAuditLogModalOpen,
    handleAuditLogModalClose,
    auditLogData,
  }
}

const useSearch = (props: ClientAreaAuditLogProps) => {
  const { clientId } = props

  const { data: gridData = [], isLoading } = useClientAreaAuditLogFetch(props)

  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [paginationState, setPaginationState] = useState<Page>(getPaginationInitialState())

  useEffect(() => {
    if (auditLogs) {
      setAuditLogs(getAuditLog())
    }
  }, [clientId, isLoading, gridData])

  const getAuditLog = () => {
    return gridData.sort((a: AuditLog, b: AuditLog) =>
      dateComparator(a.createdAt, b.createdAt, true)
    )
  }

  const handleSearch = (searchText: string) => {
    if (getAuditLog()) {
      const newResults = filterResultsByText(searchText)
      setPaginationState(getPaginationInitialState())
      setAuditLogs(newResults)
    }
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize) {
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    }

    if (auditLogs) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const filterResultsByText = (searchText: string): AuditLog[] => {
    if (getAuditLog()) {
      const auditLogItems = getAuditLog()
      const search = searchText.toLowerCase()

      if (!searchText.trim()) {
        return getAuditLog()
      }

      const filteredTypes = auditLogItems.filter((log) => log.type.toLowerCase().includes(search))
      const userTypes = auditLogItems.filter((log) => {
        if (log?.user?.name) {
          return log.user.name.toLowerCase().includes(search)
        }
        return undefined
      })
      const valueTypes = auditLogItems.filter((x) => {
        const value = getValueFactory(x).join()
        if (value) {
          return value && value.toLowerCase().includes(search)
        }
        return undefined
      })
      const fieldType = auditLogItems.filter((x) => {
        if (Array.isArray(Object.keys(x.data)) && isNotEmpty(Object.keys(x.data))) {
          return Object.keys(x.data)[0].toLowerCase().includes(search)
        }
        return undefined
      })
      const dateCreated = auditLogItems
        .filter((log) => {
          const date = formatLocalDateUTC(log.createdAt, true) ?? ''
          return date.includes(search)
        })
        .sort((a: AuditLog, b: AuditLog) => dateComparator(a.createdAt, b.createdAt, true))

      return [
        ...new Set(
          ([] as AuditLog[]).concat(
            ...dateCreated,
            ...filteredTypes,
            ...userTypes,
            ...valueTypes,
            ...fieldType
          )
        ),
      ]
    }
    return getAuditLog()
  }

  return {
    gridData,
    auditLogs,
    paginationState,
    isLoading,
    handleSearch,
    handlePageChange,
  }
}

const ClientAreaAuditLog = (props: ClientAreaAuditLogProps) => {
  const { auditLogData, handleAuditLogModalOpen, handleAuditLogModalClose } = useAuditLogData()
  const { gridData, auditLogs, isLoading, paginationState, handlePageChange, handleSearch } =
    useSearch(props)

  return (
    <>
      {!!auditLogData && (
        <Dialog open={!!auditLogData} onOpenChange={(open) => !open && handleAuditLogModalClose()}>
          <DialogContent size='lg'>
            <DialogHeader>
              <DialogTitle>JSON Data</DialogTitle>
            </DialogHeader>
            <PrettyPrintJson data={auditLogData} />
          </DialogContent>
        </Dialog>
      )}

      <DialogContent size='4xl'>
        <DialogHeader>
          <DialogTitle>Audit Log</DialogTitle>
        </DialogHeader>

        <div className='space-y-2'>
          <Label className='text-sm font-medium'>Search</Label>
          <SearchInput placeholder='Search results' onSearch={handleSearch} />
        </div>

        <div className='overflow-auto'>
          <Loading isLoading={isLoading} modalView>
            <Grid
              data={
                gridData
                  ? auditLogs.slice(
                      paginationState.skip,
                      paginationState.take + paginationState.skip
                    )
                  : []
              }
              skip={paginationState.skip}
              take={paginationState.take}
              total={auditLogs.length}
              sortable
              resizable
              pageable={pageableSettings}
              onPageChange={handlePageChange}
              style={{ zIndex: 0, overflowY: 'auto' }}
            >
              <Column
                cell={(cellProps: GridCellProps) => (
                  <td className='table-columns__content'>
                    {getFieldFactory(cellProps.dataItem).map((value) => (
                      <div className={styles.columnCellValue} key={value}>
                        {value}
                      </div>
                    ))}
                  </td>
                )}
                width={150}
                minResizableWidth={150}
                className='table-columns__content'
                title='Field'
              />
              <Column
                width={280}
                minResizableWidth={280}
                cell={(cellProps: GridCellProps) => {
                  return (
                    <td className='table-columns__content'>
                      {getValueFactory(cellProps.dataItem).map((value, index) => (
                        <div className={styles.columnCellValue} key={`${value}-${index}`}>
                          {value}
                        </div>
                      ))}
                    </td>
                  )
                }}
                title='Value'
              />
              <Column
                field='type'
                width={280}
                minResizableWidth={280}
                className='table-columns__content'
                title='Type'
              />
              <Column
                field='user.name'
                width={150}
                minResizableWidth={150}
                className='table-columns__content'
                title='User'
              />
              <Column
                field='reason'
                width={150}
                minResizableWidth={150}
                className='table-columns__content'
                title='Justification'
              />
              <Column
                title='Date'
                field='createdAt'
                width={120}
                minResizableWidth={120}
                cell={(cellProps: GridCellProps) => (
                  <td className='table-columns__date'>
                    {formatLocalDateUTC(cellProps.dataItem.createdAt, true)}
                  </td>
                )}
              />
              <Column
                width={50}
                title=''
                filterCell={() => null}
                cell={(cellProps: GridCellProps) => (
                  <td>
                    <MoreButton
                      items={[
                        {
                          name: 'JSON data',
                          'data-test': 'json-data',
                          onClick: () => handleAuditLogModalOpen(cellProps.dataItem),
                        },
                      ]}
                      data-test='more-button'
                    />
                  </td>
                )}
              />
            </Grid>
          </Loading>
        </div>
      </DialogContent>
    </>
  )
}

const PrettyPrintJson = ({ data }: { data: AuditLogData }) => (
  <div className='max-w-full overflow-auto rounded-md border border-border bg-muted/20 p-4'>
    <pre className='overflow-hidden whitespace-pre-wrap break-all text-xs'>
      {JSON.stringify(parseJsonFields(data), null, 2)}
    </pre>
  </div>
)

const parseJsonFields = (data: AuditLogData): AuditLogData | Record<string, unknown> => {
  if (typeof data === 'object' && data !== null) {
    const result: Record<string, unknown> = {}
    for (const key in data) result[key] = parseJsonFields(data[key] as AuditLogData)
    return result
  } else if (typeof data === 'string') {
    try {
      return JSON.parse(data)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return data
    }
  }
  return data
}

const getFieldFactory = (auditLog: AuditLog) => {
  if (isNotEmpty(auditLog.externalData?.name)) {
    return [auditLog.externalData?.name || '']
  }

  if (isEmpty(auditLog.data) || isString(auditLog.data)) {
    return []
  }

  if (isNameDtoWithIdPropertyWithAdditionalProperty(auditLog.data)) {
    const [key1, key2] = Object.keys(auditLog.data)
    const nameDto = auditLog?.data?.[key1]

    if (isAuditLogNameDto(nameDto)) {
      const [, name] = Object.keys(nameDto)
      return [name, key2]
    }
    return []
  }
  if (isIdPropertyWithAdditionalProperty(auditLog.data)) {
    const [, key2] = Object.keys(auditLog.data)
    return [key2]
  }
  if (isNameDtoProperty(auditLog.data)) {
    const [firstKey] = Object.keys(auditLog.data)
    return [firstKey]
  }
  if (isSingleProperty(auditLog.data)) {
    const [firstKey] = Object.keys(auditLog.data)
    return [firstKey]
  }

  return Object.entries(auditLog.data).map(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(key)
    }
    return String(key)
  })
}

const getValueFactory = (dataItem: AuditLog): string[] => {
  const externalDataValue = getExternalDataValue(dataItem)
  if (externalDataValue.length) {
    return externalDataValue
  }

  const data = dataItem?.data
  if (isEmpty(data) || isString(data)) {
    return []
  }

  if (isNameDtoWithIdPropertyWithAdditionalProperty(data)) {
    return getNameDtoWithIdPropertyWithAdditionalProperty(data)
  }

  if (isIdPropertyWithAdditionalProperty(data)) {
    return getIdPropertyWithAdditionalProperty(data)
  }

  if (isNameDtoProperty(data)) {
    return getNameDtoProperty(data)
  }

  if (isSingleProperty(data)) {
    return getSingleProperty(data)
  }

  return Object.entries(data).map(([, value]) => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }
    return String(value)
  })
}

const safeStringify = (obj: unknown): string => {
  try {
    return JSON.stringify(obj)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return '[Complex Object]'
  }
}

const stringifyAuditValue = (value: unknown): string => {
  if (typeof value === 'object' && value !== null) {
    return safeStringify(value)
  }
  return String(value)
}

const isExternalDataDeltaValue = (
  value: unknown
): value is { newValue: unknown; oldValue: unknown } => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  return 'newValue' in value && 'oldValue' in value
}

const getExternalDataValue = (auditLog: AuditLog): string[] => {
  const value = auditLog.externalData?.value
  if (value === undefined || value === null) {
    return []
  }

  if (isExternalDataDeltaValue(value)) {
    return [
      `Old: ${stringifyAuditValue(value.oldValue)}`,
      `New: ${stringifyAuditValue(value.newValue)}`,
    ]
  }

  return [stringifyAuditValue(value)]
}

const getSingleProperty = (data: AuditLogData): string[] => {
  const keys = Object.keys(data)
  const [firstKey]: string[] = keys
  const value = data?.[firstKey]
  if (value !== undefined) {
    return [typeof value === 'object' ? safeStringify(value) : String(value)]
  }
  return []
}

const getNameDtoProperty = (data: AuditLogData): string[] => {
  const keys = Object.keys(data)
  const [firstKey]: string[] = keys
  const nameDto = data?.[firstKey]

  if (isAuditLogNameDto(nameDto)) {
    const id = nameDto?.Name || nameDto?.name || ''
    const name = nameDto?.Name || nameDto?.name || ''
    return [name || id]
  }
  return [safeStringify(nameDto)]
}

const getIdPropertyWithAdditionalProperty = (data: AuditLogData): string[] => {
  const keys = Object.keys(data)
  const [, additionalProperty]: string[] = keys
  const value = data?.[additionalProperty]
  if (value !== undefined) {
    return [typeof value === 'object' ? safeStringify(value) : String(value)]
  }
  return []
}

const getNameDtoWithIdPropertyWithAdditionalProperty = (data: AuditLogData): string[] => {
  const keys = Object.keys(data)
  const [firstKey, additionalProperty]: string[] = keys
  const nameDto = data?.[firstKey]
  const value = data?.[additionalProperty]

  if (isAuditLogNameDto(nameDto)) {
    const name = nameDto?.Name || nameDto?.name || safeStringify(nameDto)
    const additionalValue = typeof value === 'object' ? safeStringify(value) : String(value)
    return [name, additionalValue]
  }
  return [safeStringify(nameDto), safeStringify(value)]
}

const isSingleProperty = (data: AuditLogData): boolean => {
  if (isEmpty(data)) return false

  const keys = Object.keys(data)

  if (keys.length !== 1) return false

  const [firstKey]: string[] = keys
  const value = data?.[firstKey]?.toString() || false

  if (firstKey) {
    return Object.prototype.hasOwnProperty.call(data, firstKey) && isNotObject(value)
  }

  return false
}

const isNameDtoProperty = (data: AuditLogData): boolean => {
  if (isEmpty(data)) {
    return false
  }

  const keys = Object.keys(data)
  const [firstKey]: string[] = keys
  const nameDto = data?.[firstKey]

  return isAuditLogNameDto(nameDto)
}

const isIdPropertyWithAdditionalProperty = (data: AuditLogData): boolean => {
  if (isEmpty(data)) {
    return false
  }

  const keys = Object.keys(data)
  const [id, additionalProperty]: string[] = keys
  return !!(
    isAuditLogNameDtoId(data) &&
    !isAuditLogNameDtoName(data) &&
    data?.[id] &&
    data?.[additionalProperty]
  )
}

const isNameDtoWithIdPropertyWithAdditionalProperty = (data: AuditLogData): boolean => {
  if (isEmpty(data)) {
    return false
  }

  return isNameDtoProperty(data) && isIdPropertyWithAdditionalProperty(data)
}

export default ClientAreaAuditLog
